import { WarningOutlined } from "@ant-design/icons";
import { Link } from "@tanstack/react-router";
import { Alert, Avatar, Button, Card, Input, Modal, Space, Table, Tag, Typography } from "antd";
import dayjs from "dayjs";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import type { RiskyEmployee } from "../types";

const { Text } = Typography;

/** Backend `reason` ni `min(3)` bilan talab qiladi (`RiskAcknowledgeSchema`). */
const SABAB_MIN = 3;

export interface RiskyEmployeeListProps {
	items: RiskyEmployee[];
	loading: boolean;
	canAcknowledge: boolean;
	isSubmitting: boolean;
	/**
	 * ⚠️ Uchinchi argument — MUVAFFAQIYAT callback'i. Modal aynan shunda
	 * yopiladi: darhol yopilsa `confirmLoading` o'lik kod bo'lardi va
	 * mutatsiya YIQILGANDA foydalanuvchi yozgan sabab yo'qolib, u qatorni
	 * hamon «Ko'rib chiqilmagan» holatda ko'rardi.
	 */
	onAcknowledge: (id: string, reason: string, onDone: () => void) => void;
}

function sana(iso: string | null): string {
	return iso ? dayjs(iso).format("DD.MM.YYYY HH:mm") : "—";
}

/** Ism va familiyaning bosh harflari (kanvasdagi avatar). */
function initsiallar(fullName: string): string {
	return fullName
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map((bolak) => bolak[0]?.toUpperCase() ?? "")
		.join("");
}

/**
 * Xavfli deb belgilangan xodimlar paneli (spec §7.2).
 *
 * ⚠️ **Tartib backendda** — ko'rib chiqilmaganlar tepada, keyin ball
 * bo'yicha kamayish (`employees.repository.ts` ning `listRisky` i).
 * Klientda QAYTA tartiblanmaydi: ikki joyda tartib bo'lsa ular
 * ajralib, rahbar sahifani yangilaganda qatorlar sakrardi.
 *
 * ⚠️⚠️ **Bayroqni «o'chirish» tugmasi YO'Q** (kontrakt §7.5). Bayroq
 * kunlik job tomonidan HISOBLANADI; qo'lda o'chirilgani keyingi
 * hisoblashda qaytib kelardi va foydalanuvchi buni nuqson deb ko'rardi.
 * Shuning uchun o'rniga buni tushuntiruvchi izoh turadi.
 *
 * ⚠️ «Ko'rib chiqdim» SABAB so'raydi — u `audit_log.reason` ga tushadi
 * (kontrakt §6.2) va sababsiz so'rov 422 bilan qaytardi.
 */
export function RiskyEmployeeList({
	items,
	loading,
	canAcknowledge,
	isSubmitting,
	onAcknowledge,
}: RiskyEmployeeListProps) {
	const { t } = useTranslation("dashboard");
	const [tanlangan, setTanlangan] = useState<RiskyEmployee | null>(null);
	const [sabab, setSabab] = useState("");

	// Bo'sh ro'yxatda panel umuman ko'rsatilmaydi — reja: «xavfli
	// xodimlar ro'yxati (agar bor bo'lsa)».
	if (!loading && items.length === 0) {
		return null;
	}

	const yopish = () => {
		setTanlangan(null);
		setSabab("");
	};

	const saqlash = () => {
		if (!tanlangan || sabab.trim().length < SABAB_MIN) {
			return;
		}

		onAcknowledge(tanlangan.id, sabab.trim(), yopish);
	};

	return (
		<Card
			title={
				<Space>
					<WarningOutlined />
					<span>{t("risky.cardTitle")}</span>
				</Space>
			}
		>
			<Alert
				type="info"
				showIcon
				className="mb-4"
				title={t("risky.infoTitle")}
				description={t("risky.infoDescription")}
			/>

			<Table<RiskyEmployee>
				dataSource={items}
				loading={loading}
				rowKey="id"
				size="small"
				pagination={false}
				columns={[
					{
						title: t("risky.columns.employee"),
						dataIndex: "fullName",
						key: "fullName",
						/*
						 * ⚠️ `/employees/$id` MAVJUD (`routeTree.tsx`,
						 * `employeeDetailRoute`, `staticData: employees:read`).
						 *
						 * ⚠️ Havola RUXSAT DARVOZASI ostida: bu ro'yxatning O'ZI
						 * `DashboardPage` da `employees:read` bilan chiziladi, ya'ni
						 * ruxsatsiz foydalanuvchida `<a>` umuman yaratilmaydi.
						 */
						render: (_: unknown, row: RiskyEmployee) => (
							<Space size={8}>
								<Avatar size={24}>{initsiallar(row.fullName)}</Avatar>
								<Link to="/employees/$id" params={{ id: row.id }}>
									{row.fullName}
								</Link>
							</Space>
						),
					},
					{
						title: t("risky.columns.riskScore"),
						dataIndex: "riskScore",
						key: "riskScore",
						width: 120,
						/*
						 * ⚠️⚠️ **0–100 FOIZLI METR CHIZILMAYDI.** Kanvas
						 * `width:${score}%` li metr ko'rsatadi, lekin `riskScore` —
						 * CHEGARALANMAGAN yig'indi (`packages/domain/src/risk.ts`:
						 * `low:1 / medium:3 / high:9`, yuqori chegara YO'Q). Metr
						 * 18 balni «18% xavfli» deb yolg'on ko'rsatardi va 100 dan
						 * katta ballda konteynerdan oshib ketardi. Ball RAQAM
						 * sifatida qoladi; shkala chegarasi — domen qarori va
						 * keyingi bosqich qarzi.
						 */
						render: (value: number) => <Text strong>{value}</Text>,
					},
					{
						title: t("risky.columns.computedAt"),
						dataIndex: "riskComputedAt",
						key: "riskComputedAt",
						width: 170,
						render: (value: string | null) => sana(value),
					},
					{
						title: t("risky.columns.status"),
						key: "holat",
						width: 180,
						/*
						 * ⚠️⚠️ **`color="green"` TAQIQLANGAN.** Yashil↔qizil
						 * juftligi deuteranopiyada ΔE 4,1 (chegara 8) — oddiy
						 * ko'rishda ular yaxshi ajraladi (ΔE 28,9), ya'ni nuqsonni
						 * ko'z bilan sezib bo'lmaydi. Yashil o'rniga AntD ko'ki
						 * (`#2a78d6` oilasi) ishlatiladi.
						 */
						render: (_: unknown, row: RiskyEmployee) =>
							row.riskAcknowledgedAt ? (
								<Tag color="blue">{t("risky.acknowledged")}</Tag>
							) : (
								<Tag color="red">{t("risky.notAcknowledged")}</Tag>
							),
					},
					{
						title: "",
						key: "amal",
						width: 150,
						render: (_: unknown, row: RiskyEmployee) =>
							canAcknowledge && !row.riskAcknowledgedAt ? (
								<Button size="small" onClick={() => setTanlangan(row)}>
									{t("risky.acknowledgeButton")}
								</Button>
							) : null,
					},
				]}
			/>

			<Modal
				open={tanlangan !== null}
				title={t("risky.modal.title")}
				okText={t("risky.modal.okText")}
				cancelText={t("risky.modal.cancelText")}
				confirmLoading={isSubmitting}
				okButtonProps={{ disabled: sabab.trim().length < SABAB_MIN }}
				onOk={saqlash}
				onCancel={yopish}
				destroyOnHidden
			>
				<Text type="secondary" className="mb-2 block">
					{t("risky.modal.prompt", { name: tanlangan?.fullName ?? "" })}
				</Text>
				<Input.TextArea
					rows={3}
					maxLength={500}
					value={sabab}
					placeholder={t("risky.modal.placeholder")}
					onChange={(event) => setSabab(event.target.value)}
				/>
			</Modal>
		</Card>
	);
}
